import {
  isTerminalDistributionStatus,
  type Connection,
  type Distribution,
  type DistributionItem,
  type Platform,
  type PlatformInfo,
  type RemoteTargetOption,
  type Source,
  type Target,
} from '@shopx-distribution/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';

import { useHandshakeContext } from '@/components/HandshakeContext.js';

import { apiFetch } from './client.js';

/** Binds every request to the current handshake token and its refresh channel. */
function useApi() {
  const { jwt, requestTokenRefresh } = useHandshakeContext();

  return useCallback(
    <T>(
      path: string,
      init: { method?: 'GET' | 'POST' | 'PATCH' | 'DELETE'; body?: unknown } = {},
    ) =>
      apiFetch<T>({
        path,
        jwt: jwt ?? '',
        method: init.method,
        body: init.body,
        refreshToken: requestTokenRefresh,
      }),
    [jwt, requestTokenRefresh],
  );
}

export function useMe() {
  const api = useApi();
  return useQuery({
    queryKey: ['me'],
    queryFn: () =>
      api<{
        tenant: { id: string; tenantDomain: string; label: string };
        userId: string;
        scopes: string[];
        environment: string;
      }>('/api/me'),
  });
}

export function useSources(search: string) {
  const api = useApi();
  return useQuery({
    queryKey: ['sources', search],
    queryFn: () =>
      api<{ sources: Source[]; totalItems: number }>(
        `/api/sources?itemsPerPage=30${search ? `&search=${encodeURIComponent(search)}` : ''}`,
      ),
  });
}

export function useSource(sourceId: string | null) {
  const api = useApi();
  return useQuery({
    queryKey: ['source', sourceId],
    enabled: Boolean(sourceId),
    queryFn: () => api<{ source: Source }>(`/api/sources/${sourceId}`),
  });
}

export interface PlatformOption extends PlatformInfo {
  platform: Platform;
  implemented: boolean;
}

export function useConnections() {
  const api = useApi();
  return useQuery({
    queryKey: ['connections'],
    queryFn: () =>
      api<{ connections: Connection[]; platforms: PlatformOption[] }>('/api/connections'),
  });
}

/** Turns on a destination that needs no credentials, target included. */
export function useEnablePlatform() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (platform: Platform) =>
      api<{ connection: Connection; alreadyEnabled?: boolean }>('/api/connections/enable', {
        method: 'POST',
        body: { platform },
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['connections'] });
      void queryClient.invalidateQueries({ queryKey: ['targets'] });
    },
  });
}

export function useAuthorizeConnection() {
  const api = useApi();
  return useMutation({
    mutationFn: (input: { platform: Platform; returnPath?: string }) =>
      api<{ authorizationUrl: string }>('/api/connections/authorize', {
        method: 'POST',
        body: input,
      }),
  });
}

export function useDisconnect() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (connectionId: string) =>
      api<{ disconnected: boolean }>(`/api/connections/${connectionId}`, { method: 'DELETE' }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['connections'] });
      void queryClient.invalidateQueries({ queryKey: ['targets'] });
    },
  });
}

export function useRemoteTargets(connectionId: string | null) {
  const api = useApi();
  return useQuery({
    queryKey: ['remote-targets', connectionId],
    enabled: Boolean(connectionId),
    queryFn: () =>
      api<{ options: RemoteTargetOption[] }>(`/api/connections/${connectionId}/remote-targets`),
  });
}

export function useTargets(includeInactive = false) {
  const api = useApi();
  return useQuery({
    queryKey: ['targets', includeInactive],
    queryFn: () => api<{ targets: Target[] }>(`/api/targets?includeInactive=${includeInactive}`),
  });
}

export function useCreateTarget() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      connectionId: string;
      name: string;
      externalTargetId: string;
      config?: Record<string, string>;
    }) => api<{ target: Target }>('/api/targets', { method: 'POST', body: input }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['targets'] }),
  });
}

export function useDeleteTarget() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (targetId: string) =>
      api<{ deleted: boolean }>(`/api/targets/${targetId}`, { method: 'DELETE' }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['targets'] }),
  });
}

export function useCreateDistribution() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { sourceId: string; layoutIds: string[]; targetIds: string[] }) =>
      api<{ distributions: Distribution[] }>('/api/distributions', {
        method: 'POST',
        body: input,
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['distributions'] }),
  });
}

/**
 * Polls while anything is still in flight. A user who lands here straight after
 * submitting would otherwise stare at `queued` until they reloaded, and the
 * Workflow usually settles within seconds.
 */
export function useDistributions(sourceId?: string) {
  const api = useApi();
  return useQuery({
    queryKey: ['distributions', sourceId ?? 'all'],
    refetchInterval: (query) =>
      query.state.data?.distributions.some(
        (distribution) => !isTerminalDistributionStatus(distribution.status),
      )
        ? 3000
        : false,
    queryFn: () =>
      api<{ distributions: Distribution[] }>(
        `/api/distributions${sourceId ? `?sourceId=${encodeURIComponent(sourceId)}` : ''}`,
      ),
  });
}

/** Polls while the run is in flight and stops once it settles. */
export function useDistribution(distributionId: string | null) {
  const api = useApi();
  return useQuery({
    queryKey: ['distribution', distributionId],
    enabled: Boolean(distributionId),
    refetchInterval: (query) => {
      const status = query.state.data?.distribution.status;
      return status === 'queued' || status === 'running' ? 2000 : false;
    },
    queryFn: () =>
      api<{ distribution: Distribution; items: DistributionItem[] }>(
        `/api/distributions/${distributionId}`,
      ),
  });
}
